import { useCallback, useEffect, useState } from "react";
import { useShowSnackbar } from "hooks/useShowSnackbar";
import { useNavigate } from "hooks/useCustomNavigation";
import { useCartContext } from "context/cart-context";
import { INTERNAL_PATHS } from "routes/paths";
import {
  checkout as checkoutRequest,
  getCheckoutData as getCheckoutDataRequest
} from "./requests";
import { FEEDBACK_MESSAGES, INITIAL_CHECKOUT_CONTEXT_STATE } from "./constants";
import { useAppContext } from "context/app-context";

export const useCheckoutData = ({ device }) => {
  const {
    sessionInfo: { userID }
  } = useAppContext();
  const { emptyCart } = useCartContext();
  const navigate = useNavigate();
  const [checkoutData, setCheckoutData] = useState(
    INITIAL_CHECKOUT_CONTEXT_STATE.checkoutData
  );
  const [isLoading, setIsLoading] = useState(false);
  const [disableSubmitPayment, setDisableSubmitPayment] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState("");
  const { addNotification } = useShowSnackbar();

  const getCheckoutData = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await getCheckoutDataRequest();

      const isEmptyCart = !response.data.cards.length;
      if (isEmptyCart) navigate(INTERNAL_PATHS.BUY_GIFT_CARDS);

      setCheckoutData(response.data);
    } catch (err) {
      addNotification({
        message: FEEDBACK_MESSAGES.CHECKOUT_FETCH_ERROR,
        variant: "error"
      });
    } finally {
      setIsLoading(false);
    }
  }, [addNotification, navigate]);

  const handleOrderFailure = useCallback(() => {
    addNotification({ message: FEEDBACK_MESSAGES.TIMEOUT, variant: "error" });
    setIsLoading(false);

    setTimeout(() => {
      navigate(INTERNAL_PATHS.BUY_GIFT_CARDS);
    }, 3000);
  }, [navigate, addNotification]);

  const handleCheckout = useCallback(async () => {
    if (selectedPayment) {
      setIsLoading(true);
      try {
        const response = await checkoutRequest({ selectedPayment, device });

        if (response.status === 422) return handleOrderFailure();

        const { data } = response;
        if (data.success && data.data.order_number) {
          const orderUrl = INTERNAL_PATHS.ORDERS.replace(
            ":orderId",
            data.data.order_number
          );
          emptyCart({ userID });
          navigate(orderUrl);
        }
      } catch (err) {
        addNotification({
          message: FEEDBACK_MESSAGES.CHECKOUT_ERROR,
          variant: "error"
        });
        setIsLoading(false);
      }
    }
  }, [
    device,
    userID,
    selectedPayment,
    navigate,
    addNotification,
    handleOrderFailure,
    emptyCart
  ]);

  useEffect(() => {
    getCheckoutData();
  }, [getCheckoutData]);

  return {
    isLoading,
    checkoutData,
    selectedPayment,
    disableSubmitPayment,
    getCheckoutData,
    setPayment: setSelectedPayment,
    setDisableSubmitPayment,
    handleOrderFailure,
    handleCheckout
  };
};
